import React, { useState, useMemo } from 'react';
import { useFixedPageStack } from '../../client/hooks/useFixedPageStack';
import { useQuery, useAction } from 'wasp/client/operations';
import { getFoodCourtItems, deleteFoodCourtItem } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { withSectionProtection } from '../../client/components/ProtectedSection';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import { isAdministrator } from '../../client/lib/platformRole';
import { 
  EnhancedActionButton, 
  EnhancedIconButton, 
  useResponsiveLayout
} from '../../components/ui/enhanced-page-layout';
import { TableFilterLayout } from '../../components/ui/table-filter-layout';
import { DetailListItem } from '../../components/ui/detail-list-item';
import { Search, Filter, Plus, Eye, Edit, Trash2, Tag, Phone, Clock, Utensils, Grid3X3, List, Table } from 'lucide-react';
import { MenuViewerButton } from './MenuViewer';
import { hasFoodCourtMenu } from './menuTypes';

function FoodCourtPage() {
  const { data: user } = useAuth();
  const showAdminColumns = isAdministrator(user);
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const routerLocation = useLocation();
  const locSlug = resolveLocationSlug(routerLocation.pathname, params.slug, selectedLocation);
  const deleteFoodCourtItemFn = useAction(deleteFoodCourtItem);
  
  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'title'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  // Use responsive layout hook
  const { viewMode, setViewMode, isMobile, shouldCollapseSidebar, hideViewSwitcher } = useResponsiveLayout('table');

  // Fetch items with hub aggregation support
  const { data: items = [], isLoading, error } = useQuery(getFoodCourtItems, {
    locationIds: getLocationIdsForQuery(),
    filters: {
      type: 'FOOD_COURT',
    }
  });

  const hasListContent = !isLoading && !error && items.length > 0;
  const fixedStack = useFixedPageStack({
    secondaryKind: !hasListContent
      ? 'none'
      : viewMode === 'table'
        ? 'table-filter'
        : 'ref',
    secondaryRefDeps: [viewMode, items.length, isLoading, error, showFilters],
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((item: any) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.metadata?.cuisine?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((item: any) => 
        item.metadata?.category === selectedCategory
      );
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        filtered = [...filtered].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'title':
        filtered = [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const tableColumns = useMemo(() => {
    const columns = [
      { title: 'Item', span: 3 },
      { title: 'Category', span: 2 },
      { title: 'Cuisine', span: 2 },
      { title: 'Menu', span: 1 },
      { title: 'Availability', span: showAdminColumns ? 2 : 4 },
    ];
    if (showAdminColumns) {
      columns.push({ title: 'Author', span: 1 }, { title: 'Actions', span: 1 });
    }
    return columns;
  }, [showAdminColumns]);

  const tableFilters = useMemo(() => {
    const filters = [
      {
        span: 3,
        content: (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search food items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        ),
      },
      {
        span: 2,
        content: (
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All</option>
            <option value="FOOD">Food</option>
            <option value="BEVERAGE">Beverage</option>
            <option value="DESSERT">Dessert</option>
            <option value="SNACK">Snack</option>
          </select>
        ),
      },
      {
        span: 2,
        content: (
          <input
            type="text"
            placeholder="All cuisines"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        ),
      },
      {
        span: 1,
        content: (
          <select
            className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All</option>
            <option value="with-menu">Has menu</option>
          </select>
        ),
      },
      {
        span: showAdminColumns ? 2 : 4,
        content: (
          <select
            className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All</option>
            <option value="AVAILABLE">Available</option>
            <option value="LIMITED">Limited</option>
            <option value="OUT_OF_STOCK">Out of Stock</option>
          </select>
        ),
      },
    ];

    if (showAdminColumns) {
      filters.push(
        {
          span: 1,
          content: (
            <select
              value={sortBy === 'newest' || sortBy === 'oldest' ? sortBy : 'newest'}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
            </select>
          ),
        },
        {
          span: 1,
          content: <div className="h-10" />,
        }
      );
    }

    return filters;
  }, [searchTerm, selectedCategory, sortBy, showAdminColumns]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteFoodCourtItemFn({ itemId });
        // The query will automatically refetch due to Wasp's reactivity
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item. Please try again.');
      }
    }
  };

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Location Selected</h2>
            <p className="text-gray-600">Please select a location to view food court items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'FOOD': 'bg-secondary/10 text-secondary border border-secondary/20',
      'BEVERAGE': 'bg-primary/10 text-primary border border-primary/20',
      'DESSERT': 'bg-accent/10 text-accent border border-accent/20',
      'SNACK': 'bg-warning/10 text-warning border border-warning/20'
    };
    return colors[category as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      'AVAILABLE': 'bg-success/10 text-success border border-success/20',
      'LIMITED': 'bg-warning/10 text-warning border border-warning/20',
      'OUT_OF_STOCK': 'bg-destructive/10 text-destructive border border-destructive/20'
    };
    return colors[availability as keyof typeof colors] || 'bg-muted text-muted-foreground border border-border';
  };

  return (
    <DashboardLayout 
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div
        className={fixedStack.pageContainerClassName}
        style={fixedStack.pageContainerStyle}
      >
        {/* Fixed Main Header */}
        <div
          ref={fixedStack.pageHeaderRef}
          className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Food Court</h1>
                <p className="text-sm text-gray-600 mt-1">Discover food and beverages available at {selectedLocation.name} ({filteredItems.length} items)</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden lg:inline">Filters</span>
                </button>
                {user && (
                  <EnhancedActionButton
                    onClick={() => navigate(locationSectionPath(locSlug, 'food-court', 'add'))}
                    icon={Plus}
                    label={<span className="hidden lg:inline">Add Item</span>}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header (Conditional) */}
        {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
          <TableFilterLayout
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10`}
            showFilters={showFilters}
            onHeightChange={fixedStack.onTableFilterHeightChange}
            columns={tableColumns}
            filters={tableFilters}
          />
        )}

        {/* Fixed Filters for non-table views */}
        {!isLoading && !error && items.length > 0 && viewMode !== 'table' && (
          <div
            ref={fixedStack.secondaryStackRef}
            className={`fixed ${fixedStack.secondaryFixedTopClass} left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300`}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search food items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="FOOD">Food</option>
                  <option value="BEVERAGE">Beverage</option>
                  <option value="DESSERT">Dessert</option>
                  <option value="SNACK">Snack</option>
                </select>
                
                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Title A-Z</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content — padding matches measured fixed stacks only (no duplicate --header-height) */}
        <div
          className={fixedStack.scrollContainerClassName}
          style={fixedStack.scrollContainerStyle}
        >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading items...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Utensils className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Items</h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredItems.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Utensils className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Found</h3>
                  <p className="text-gray-600">
                    {searchTerm || selectedCategory !== 'all' 
                      ? 'Try adjusting your search or filters to find what you\'re looking for.' 
                      : 'Be the first to add a food item in this location!'
                    }
                  </p>
                </div>
              </div>
            )}

        {/* Content */}
        {!isLoading && !error && filteredItems.length > 0 && (
          <>
            {/* Cards View - Use enhanced layout with padding */}
            {viewMode === 'cards' && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(item.metadata?.category)}`}>
                              {item.metadata?.category}
                            </span>
                            {item.metadata?.availability && (
                              <span className={`px-2 py-1 text-xs font-medium rounded ${getAvailabilityColor(item.metadata.availability)}`}>
                                {item.metadata.availability.replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          {hasFoodCourtMenu(item.metadata) && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <MenuViewerButton metadata={item.metadata} itemTitle={item.title} />
                            </div>
                          )}
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                          {item.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {item.description}
                        </p>

                        {item.metadata?.cuisine && (
                          <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                            <Utensils className="w-3 h-3" />
                            {item.metadata.cuisine}
                          </div>
                        )}

                        {item.metadata?.dietaryInfo && item.metadata.dietaryInfo.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {item.metadata.dietaryInfo.map((diet: string, index: number) => (
                              <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                {diet}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-gray-500">
                            by {item.author.name || item.author.email}
                          </div>
                          <div 
                            className="flex items-center gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                              icon={Eye}
                              title="View details"
                            />
                            {user && item.authorId === user.id && (
                              <>
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id, 'edit'))}
                                  icon={Edit}
                                  title="Edit item"
                                />
                                <EnhancedIconButton
                                  onClick={() => handleDelete(item.id)}
                                  icon={Trash2}
                                  title="Delete item"
                                  variant="danger"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detail List View */}
            {viewMode === 'list' && (
              <div className="pb-2">
                {filteredItems.map((item) => (
                  <DetailListItem
                    key={item.id}
                    title={item.title}
                    description={item.description}
                    website={(item.metadata as any)?.website ?? null}
                    onSelect={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                    meta={
                      <>
                        {item.metadata?.category && (
                          <span className={`px-2 py-0.5 rounded ${getCategoryColor(item.metadata.category)}`}>
                            {item.metadata.category}
                          </span>
                        )}
                        {item.metadata?.availability && (
                          <span className={`px-2 py-0.5 rounded ${getAvailabilityColor(item.metadata.availability)}`}>
                            {item.metadata.availability.replace('_', ' ')}
                          </span>
                        )}
                        {item.metadata?.cuisine && (
                          <span className="inline-flex items-center gap-1">
                            <Utensils className="w-3 h-3" />
                            {item.metadata.cuisine}
                          </span>
                        )}
                        {hasFoodCourtMenu(item.metadata) && (
                          <span onClick={(e) => e.stopPropagation()}>
                            <MenuViewerButton metadata={item.metadata} itemTitle={item.title} />
                          </span>
                        )}
                      </>
                    }
                    trailing={
                      <>
                        <EnhancedIconButton
                          onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                          icon={Eye}
                          title="View details"
                        />
                        {user && item.authorId === user.id && (
                          <>
                            <EnhancedIconButton
                              onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id, 'edit'))}
                              icon={Edit}
                              title="Edit item"
                            />
                            <EnhancedIconButton
                              onClick={() => handleDelete(item.id)}
                              icon={Trash2}
                              title="Delete item"
                              variant="danger"
                            />
                          </>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="w-full">
                {/* Table Body - No internal scroll */}
                <div className="divide-y divide-gray-200">
                  {filteredItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                      onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                    >
                      <div className="grid grid-cols-12 gap-4 items-start">
                        <div className="col-span-3">
                          <div className="text-sm font-medium text-gray-900">
                            {item.title}
                          </div>
                          <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {item.description}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">
                            {item.metadata?.category}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-sm text-gray-600">
                            {item.metadata?.cuisine || '-'}
                          </span>
                        </div>
                        <div className="col-span-1" onClick={(e) => e.stopPropagation()}>
                          <MenuViewerButton metadata={item.metadata} itemTitle={item.title} />
                        </div>
                        <div className={showAdminColumns ? 'col-span-2' : 'col-span-4'}>
                          <span className="text-sm text-gray-600">
                            {item.metadata?.availability ? item.metadata.availability.replace('_', ' ') : '-'}
                          </span>
                        </div>
                        {showAdminColumns && (
                          <>
                            <div className="col-span-1">
                              <span className="text-sm text-gray-500">
                                {item.author.name || item.author.email}
                              </span>
                            </div>
                            <div className="col-span-1">
                              <div 
                                className="flex items-center gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id))}
                                  icon={Eye}
                                  title="View details"
                                />
                                {user && item.authorId === user.id && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() => navigate(locationSectionPath(locSlug, 'food-court', item.id, 'edit'))}
                                      icon={Edit}
                                      title="Edit item"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(item.id)}
                                      icon={Trash2}
                                      title="Delete item"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default withSectionProtection(FoodCourtPage, 'food-court');



